import { forwardRef, type InputHTMLAttributes, useId } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, indeterminate, className, id: providedId, ...props }, ref) => {
    const generatedId = useId();
    const id = providedId || generatedId;

    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex cursor-pointer items-center gap-2',
          props.disabled && 'cursor-not-allowed opacity-60',
          className
        )}
      >
        <div className="relative">
          <input
            ref={ref}
            type="checkbox"
            id={id}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded border-2 transition-colors',
              'border-border bg-white',
              'peer-checked:border-primary peer-checked:bg-white peer-checked:text-primary peer-checked:[&_svg]:opacity-100',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/20',
              'peer-disabled:cursor-not-allowed peer-disabled:bg-gray-50',
              indeterminate && 'border-primary bg-primary text-white'
            )}
          >
            {indeterminate ? (
              <Minus className="h-3 w-3 text-white" />
            ) : (
              <Check className="h-3 w-3 opacity-0" />
            )}
          </div>
        </div>
        {label && (
          <span className="select-none text-sm text-foreground">{label}</span>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';
